import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
} from 'react';
import { format } from 'date-fns';
import type { NavigateFunction } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { sampleService, type Sample, type SampleListParams } from '../services/sample.service';
import {
  DataTableConfig,
  destroyDataTableIfPresent,
  shouldRebuildDataTable,
} from '../utils/dataTableLifecycle';
import { ensureDataTablesNaturalJaSort } from '../utils/dataTablesNaturalSort';
import {
  dataTablesLengthA11yLabels,
  dockSampleListExportButtons,
  syncDataTablesLengthA11y,
} from '../utils/sampleListExportDock';
import { syncSampleRowMoreOpenForViewport } from '../utils/sampleRowMoreViewport';
import {
  buildSampleManagerDataTableColumns,
  SAMPLE_MANAGER_COLVIS_COLUMNS,
  SAMPLE_MANAGER_DT_STATE_PREFIX,
  SAMPLE_MANAGER_LAST_USED_COLUMN_INDEX,
  SAMPLE_MANAGER_REGISTRANT_COLUMN_INDEX,
} from '../utils/sampleManagerDataTableColumns';
import { syncSampleManagerTableColgroup } from '../utils/syncDataTableColgroup';
import { injectDataTableCellSeparators } from '../utils/injectDataTableCellSeparators';
import {
  getSampleListRowTone,
  sampleListRowToneClass,
} from '../utils/sampleUsageRecords';
import { parseCaseLocationFromNavElement } from '../utils/caseLocationUtils';
import {
  navigateToCaseManagerWithCell,
  navigateToSampleDetail,
} from '../utils/samplePageNavigation';
import { deliverBlobToUser } from '../utils/downloadBlob';
import { logger, toLoggableError } from '../utils/logger';
import {
  buildDataTablesExportButtons,
  buildDataTablesLanguage,
  getKensukeJQuery,
  type DataTablesApi,
} from '../utils/jqueryDatatablesShim';

const ROW_ACTION_SELECTORS = [
  '.usage-record-btn',
  '.view-btn',
  '.sample-name-open',
  'a.case-manager-nav-link',
  '.edit-btn',
  '.delete-btn',
] as const;

function closeRowMoreMenu(el: HTMLElement) {
  el.closest('tr')?.querySelector('details.sample-row-more')?.removeAttribute('open');
}

type UseSampleManagerDataTableArgs = {
  data: unknown;
  displayData: Sample[];
  hasData: boolean;
  tableRef: RefObject<HTMLTableElement | null>;
  isAdmin: boolean;
  adminViewAll: boolean;
  userId: number | undefined;
  language: string;
  t: TFunction;
  filtersRef: MutableRefObject<SampleListParams>;
  selectedSampleIdsRef: MutableRefObject<Set<number>>;
  adminViewAllRef: MutableRefObject<boolean>;
  includeSharedStorageSamplesRef: MutableRefObject<boolean | undefined>;
  navigate: NavigateFunction;
  setError: (message: string) => void;
  setSelectedSample: Dispatch<SetStateAction<Sample | null>>;
  setShowDetailModal: (show: boolean) => void;
  setShowUsageModal: (show: boolean) => void;
  onEdit: (sample: Sample) => void;
  onDelete: (sample: Sample) => void;
};

/**
 * サンプル一覧 DataTables の初期化・行操作・リサイズ調整。
 * StrictMode 二重破棄を避けるため cleanup では destroy しない（次マウントで再判定）。
 */
export function useSampleManagerDataTable({
  data,
  displayData,
  hasData,
  tableRef,
  isAdmin,
  adminViewAll,
  userId,
  language,
  t,
  filtersRef,
  selectedSampleIdsRef,
  adminViewAllRef,
  includeSharedStorageSamplesRef,
  navigate,
  setError,
  setSelectedSample,
  setShowDetailModal,
  setShowUsageModal,
  onEdit,
  onDelete,
}: UseSampleManagerDataTableArgs) {
  const dataTableRef = useRef<DataTablesApi | null>(null);
  const previousConfigRef = useRef<DataTableConfig | null>(null);

  const unbindRowActions = useCallback((tableElement: HTMLTableElement | null) => {
    const $ = getKensukeJQuery();
    if (!$ || !tableElement) return;
    for (const selector of ROW_ACTION_SELECTORS) {
      $(tableElement).off('click', selector);
    }
  }, []);

  const destroyTable = useCallback(() => {
    const currentElement = tableRef.current;
    unbindRowActions(currentElement);
    destroyDataTableIfPresent(currentElement);
    dataTableRef.current = null;
    previousConfigRef.current = null;
  }, [tableRef, unbindRowActions]);

  useLayoutEffect(() => {
    if (data === undefined) return;

    if (!hasData || !displayData || !Array.isArray(displayData) || displayData.length === 0) {
      if (dataTableRef.current) destroyTable();
      // カスタム空状態と二重にならないよう、破棄後の残行も消す
      const tbody = tableRef.current?.tBodies[0];
      if (tbody) tbody.replaceChildren();
      return;
    }

    const tableElement = tableRef.current;
    if (!tableElement) {
      if (dataTableRef.current) destroyTable();
      return;
    }

    const cardEl = tableElement.closest('.card');
    const tableWrapEl = tableElement.closest('.table-responsive');
    if (!cardEl || !tableWrapEl) {
      logger.warn(
        '[SampleManager] useLayoutEffect: parent container missing; skipping table re-init',
        {
          tableInDOM: document.body.contains(tableElement),
          cardExists: !!cardEl,
          parentExists: !!tableWrapEl,
        }
      );
      return;
    }

    const prev = previousConfigRef.current;
    const nextConfig: DataTableConfig = {
      language,
      isAdmin,
      hasData,
      adminViewAll,
    };

    const $probe = getKensukeJQuery();
    if ($probe?.fn.DataTable?.isDataTable(tableElement)) {
      if (!shouldRebuildDataTable(prev, nextConfig)) {
        if (dataTableRef.current && Array.isArray(displayData)) {
          try {
            dataTableRef.current.clear();
            dataTableRef.current.rows.add(displayData);
            dataTableRef.current.draw();
          } catch (err) {
            logger.error('[SampleManager] DataTables row update failed', toLoggableError(err));
          }
        }
        return;
      }
      destroyTable();
    }

    try {
      const $ = getKensukeJQuery();
      if (typeof window === 'undefined' || !$?.fn.DataTable) {
        setError(t('common.datatables_not_loaded'));
        return;
      }

      destroyDataTableIfPresent(tableElement);
      ensureDataTablesNaturalJaSort();

      const dt = $(tableElement).DataTable({
        data: displayData,
        columns: buildSampleManagerDataTableColumns({
          t,
          language,
          userId,
          isAdmin,
          adminViewAll,
          selectedSampleIds: selectedSampleIdsRef.current,
        }),
        language: buildDataTablesLanguage(t),
        paging: false,
        searching: false,
        info: false,
        lengthChange: false,
        order: [[SAMPLE_MANAGER_LAST_USED_COLUMN_INDEX, 'desc']],
        dom: 'Brt',
        stateSave: true,
        stateDuration: 30 * 24 * 60 * 60,
        stateSaveCallback: function (settings: { sInstance: string }, state: unknown) {
          try {
            localStorage.setItem(
              SAMPLE_MANAGER_DT_STATE_PREFIX + settings.sInstance,
              JSON.stringify(state)
            );
          } catch (e) {
            logger.warn('DataTables state save failed', { detail: String(e) });
          }
        },
        stateLoadCallback: function (settings: { sInstance: string }) {
          try {
            const saved = localStorage.getItem(SAMPLE_MANAGER_DT_STATE_PREFIX + settings.sInstance);
            if (!saved) return null;
            const parsed = JSON.parse(saved);
            if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.columns)) {
              return parsed;
            }
            if (parsed.columns[SAMPLE_MANAGER_REGISTRANT_COLUMN_INDEX]) {
              parsed.columns[SAMPLE_MANAGER_REGISTRANT_COLUMN_INDEX].visible = Boolean(
                isAdmin && adminViewAllRef.current
              );
            }
            return parsed;
          } catch (e) {
            logger.warn('DataTables state load failed', { detail: String(e) });
            return null;
          }
        },
        buttons: buildDataTablesExportButtons({
          t,
          asCollection: true,
          colvisOutsideCollection: true,
          colvisColumns: SAMPLE_MANAGER_COLVIS_COLUMNS,
          /** document.title / head>title 依存禁止（他画面の Helmet が残ると誤名になる） */
          filename: () => `samples_${format(new Date(), 'yyyy-MM-dd')}`,
          excelTitle: t('navigation.sample_management', { defaultValue: 'Sample management' }),
          extraPdfButton: {
            text: t('common.pdf'),
            className: 'buttons-pdf',
            action: async function () {
              try {
                const currentFilters = { ...filtersRef.current };
                const selectedIds = Array.from(selectedSampleIdsRef.current);
                const blob = await sampleService.exportPdf({
                  ...currentFilters,
                  adminViewAll: adminViewAllRef.current || undefined,
                  includeSharedStorageSamples: includeSharedStorageSamplesRef.current,
                  ...(selectedIds.length > 0
                    ? { sampleIds: selectedIds }
                    : { page: 1, limit: 1000 }),
                });
                deliverBlobToUser(blob, `samples_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
              } catch (error: unknown) {
                logger.error('PDF export failed', toLoggableError(error));
                setError(t('sample.export_pdf_failed'));
              }
            },
          },
        }),
        responsive: false,
        autoWidth: false,
        scrollX: false,
        scrollCollapse: false,
        createdRow: (row: HTMLElement, rowData: Sample) => {
          const toneClass = sampleListRowToneClass(getSampleListRowTone(rowData));
          if (toneClass) row.classList.add(toneClass);
          injectDataTableCellSeparators(row);
          syncSampleRowMoreOpenForViewport(row);
        },
        initComplete: function (this: { api: () => DataTablesApi }) {
          // DataTables は initComplete をコンストラクタ返却前に呼ぶ。
          // 外側の `const dt = …` を触ると TDZ（Cannot access before initialization）になるため this.api() を使う。
          const api = this.api();
          syncSampleManagerTableColgroup(tableElement);
          syncSampleRowMoreOpenForViewport(tableElement);
          dockSampleListExportButtons();
          syncDataTablesLengthA11y(
            '#sample-manager-table-wrapper .dataTables_wrapper',
            dataTablesLengthA11yLabels(t)
          );
          setTimeout(() => {
            try {
              api.columns.adjust();
            } catch (err) {
              logger.warn('[SampleManager] column adjust failed', { detail: String(err) });
            }
            syncSampleManagerTableColgroup(tableElement);
            syncSampleRowMoreOpenForViewport(tableElement);
            dockSampleListExportButtons();
            syncDataTablesLengthA11y(
              '#sample-manager-table-wrapper .dataTables_wrapper',
              dataTablesLengthA11yLabels(t)
            );
          }, 100);
        },
        drawCallback: function () {
          syncSampleManagerTableColgroup(tableElement);
          syncSampleRowMoreOpenForViewport(tableElement);
        },
      });

      const bindRowAction = (
        selector: string,
        handler: (rowData: Sample | undefined, el: HTMLElement, e: JQuery.Event) => void,
        opts?: { closeMore?: boolean; skipIfDisabled?: boolean }
      ) => {
        $(tableElement).on('click', selector, function (this: HTMLElement, e: JQuery.Event) {
          e.preventDefault();
          const $target = $(this);
          if (opts?.skipIfDisabled && $target.prop('disabled')) return;
          const rowData = dt.row($target.closest('tr')).data() as Sample | undefined;
          if (opts?.closeMore) closeRowMoreMenu(this);
          handler(rowData, this, e);
        });
      };

      bindRowAction(
        '.usage-record-btn',
        (rowData) => {
          if (!rowData) return;
          setSelectedSample(rowData);
          setShowUsageModal(true);
        },
        { skipIfDisabled: true }
      );

      // 名前タップ: クイック詳細モーダル / 行の「詳細」: フルページ（導線を分ける）
      bindRowAction('.sample-name-open', (rowData) => {
        if (!rowData) return;
        setSelectedSample(rowData);
        setShowDetailModal(true);
      });

      bindRowAction(
        '.view-btn',
        (rowData) => {
          if (!rowData) return;
          navigateToSampleDetail(navigate, rowData.id);
        },
        { closeMore: true }
      );

      $(tableElement).on('click', 'a.case-manager-nav-link', function (this: HTMLElement, e: JQuery.Event) {
        e.preventDefault();
        const cell = parseCaseLocationFromNavElement(this);
        if (cell) {
          navigateToCaseManagerWithCell(navigate, cell);
        } else {
          navigate('/samples/cases');
        }
      });

      bindRowAction(
        '.edit-btn',
        (rowData) => {
          if (rowData) onEdit(rowData);
        },
        { closeMore: true }
      );

      bindRowAction(
        '.delete-btn',
        (rowData) => {
          if (rowData) onDelete(rowData);
        },
        { closeMore: true }
      );

      dataTableRef.current = dt;
      previousConfigRef.current = {
        language,
        isAdmin,
        hasData,
        adminViewAll,
      };
    } catch (err) {
      logger.error('[SampleManager] DataTables init failed', toLoggableError(err));
      setError(t('common.table_init_failed'));
    }

    return () => {
      // StrictMode 二重破棄防止 — destroy は次のマウント／再構築時
    };
  }, [
    data,
    hasData,
    displayData,
    isAdmin,
    adminViewAll,
    userId,
    language,
    t,
    tableRef,
    filtersRef,
    selectedSampleIdsRef,
    adminViewAllRef,
    includeSharedStorageSamplesRef,
    navigate,
    setError,
    setSelectedSample,
    setShowDetailModal,
    setShowUsageModal,
    onEdit,
    onDelete,
    destroyTable,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const $ = getKensukeJQuery();
    if (!$) return;

    const adjustAndDock = () => {
      try {
        if (dataTableRef.current) {
          dataTableRef.current.columns.adjust();
          const tableElement = tableRef.current;
          if (tableElement) {
            syncSampleRowMoreOpenForViewport(tableElement);
            const $table = $(tableElement);
            const $wrapper = $table.closest('.dataTables_wrapper');
            if ($wrapper.length) {
              $wrapper.css({ width: '100%', overflowX: 'visible' });
              $table.css({ width: '100%' });
            }
          }
        }
        dockSampleListExportButtons();
      } catch (err) {
        logger.warn('[SampleManager] column adjust on resize failed', { detail: String(err) });
      }
    };

    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(adjustAndDock, 250);
    };

    window.addEventListener('resize', handleResize);
    // スロット出現・DT 立上げ後に1回（ページ側の三重 dock をここへ集約）
    const timer = window.setTimeout(adjustAndDock, 0);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.clearTimeout(timer);
      clearTimeout(resizeTimer);
    };
  }, [language, tableRef, hasData, displayData]);

  return { dataTableRef };
}
